import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import {toast} from "react-toastify";
// @ts-ignore
import {EnvelopeOpen, ArrowClockwise, Plus, Trash, CheckCircle, Clock, WarningCircle} from "@phosphor-icons/react";
import {getAxiosBaseUrl} from "../../config";

// Map a verification_status string from the backend to a UI bucket.
function bucketStatus(verification_status, verified) {
	const v = (verification_status || '').toString();
	if (verified === true || /success/i.test(v)) return 'Verified';
	if (/fail/i.test(v)) return 'Failed';
	// Pending, NotStarted, In Progress, etc. → Pending bucket
	return 'Pending';
}

function StatusBadge({status}) {
	const map = {
		Verified: {cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle},
		Pending:  {cls: 'bg-amber-100 text-amber-800',     Icon: Clock},
		Failed:   {cls: 'bg-red-100 text-red-800',         Icon: WarningCircle},
	};
	const {cls, Icon} = map[status] || map.Pending;
	return (
		<span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${cls}`}>
			<Icon size={12} weight="bold"/>
			{status}
		</span>
	);
}

function isValidEmail(email) {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
}

export default function Identities() {
	const verifiedemail = sessionStorage.getItem("verifiedemail") || '';

	const [items, setItems] = useState([]); // [{email, status}]
	const [loading, setLoading] = useState(true);
	const [refreshingAll, setRefreshingAll] = useState(false);
	const [rowBusy, setRowBusy] = useState({}); // {email: 'check'|'remove'}

	const [newEmail, setNewEmail] = useState('');
	const [adding, setAdding] = useState(false);

	const formRef = useRef(null);

	const checkOne = async (email) => {
		try {
			const res = await axios.post('/check_identity', {email});
			const d = res && res.data ? res.data : {};
			return bucketStatus(d.verification_status, d.verified);
		} catch (err) {
			console.log('check_identity error', err);
			return 'Pending';
		}
	};

	const loadAll = async () => {
		setLoading(true);
		try {
			axios.defaults.baseURL = getAxiosBaseUrl();
			const res = await axios.post('/list_identities', {user_email: verifiedemail});
			const list = (res && res.data && Array.isArray(res.data.identities)) ? res.data.identities : [];
			// Fetch all statuses in parallel
			const statuses = await Promise.all(list.map((e) => checkOne(e)));
			const next = list.map((email, i) => ({email, status: statuses[i]}));
			setItems(next);
		} catch (err) {
			console.log('list_identities error', err);
			toast.error('Could not load sender identities');
			setItems([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		axios.defaults.baseURL = getAxiosBaseUrl();
		loadAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const refreshAll = async () => {
		if (!items.length) return;
		setRefreshingAll(true);
		try {
			axios.defaults.baseURL = getAxiosBaseUrl();
			const statuses = await Promise.all(items.map((it) => checkOne(it.email)));
			setItems(items.map((it, i) => ({...it, status: statuses[i]})));
			toast.success('Statuses refreshed');
		} finally {
			setRefreshingAll(false);
		}
	};

	const refreshRow = async (email) => {
		setRowBusy(prev => ({...prev, [email]: 'check'}));
		try {
			axios.defaults.baseURL = getAxiosBaseUrl();
			const status = await checkOne(email);
			setItems(curr => curr.map(it => it.email === email ? {...it, status} : it));
		} finally {
			setRowBusy(prev => {
				const next = {...prev};
				delete next[email];
				return next;
			});
		}
	};

	const removeRow = async (email) => {
		const ok = window.confirm(`Remove ${email} from your verified senders? You won't be able to send from this address until you re-verify it.`);
		if (!ok) return;
		setRowBusy(prev => ({...prev, [email]: 'remove'}));
		try {
			axios.defaults.baseURL = getAxiosBaseUrl();
			await axios.post('/remove_identity', {email});
			toast.success(`Removed ${email}`);
			await loadAll();
		} catch (err) {
			console.log('remove_identity error', err);
			toast.error('Could not remove identity');
		} finally {
			setRowBusy(prev => {
				const next = {...prev};
				delete next[email];
				return next;
			});
		}
	};

	const addIdentity = async (e) => {
		if (e && e.preventDefault) e.preventDefault();
		const email = (newEmail || '').trim();
		if (!isValidEmail(email)) {
			toast.error('Enter a valid email address');
			return;
		}
		setAdding(true);
		try {
			axios.defaults.baseURL = getAxiosBaseUrl();
			await axios.post('/onboard_email', {user_email: email});
			toast.success('Verification email sent — check your inbox');
			setNewEmail('');
			await loadAll();
		} catch (err) {
			console.log('onboard_email error', err);
			toast.error('Could not start verification');
		} finally {
			setAdding(false);
		}
	};

	const scrollToForm = () => {
		if (formRef.current) {
			formRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
			const input = formRef.current.querySelector('input[type="email"]');
			if (input) input.focus();
		}
	};

	return (
		<div className="min-h-screen bg-slate-50">
			<div className="max-w-4xl mx-auto px-6 py-10">
				{/* Hero header */}
				<div className="flex items-start justify-between gap-4 mb-8">
					<div>
						<h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Sender identities</h1>
						<p className="text-slate-600 mt-2 max-w-2xl">
							AWS SES requires every sender address to be a verified identity. Add an email below — we'll send a verification link to that inbox. Once you click it, you can use that address as the &quot;from&quot; on your campaigns.
						</p>
					</div>
					<button
						type="button"
						onClick={refreshAll}
						disabled={refreshingAll || loading || items.length === 0}
						className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition shrink-0">
						<ArrowClockwise size={15} weight="bold" className={refreshingAll ? 'animate-spin' : ''}/>
						{refreshingAll ? 'Refreshing…' : 'Refresh all'}
					</button>
				</div>

				{/* Add form */}
				<form
					ref={formRef}
					onSubmit={addIdentity}
					className="rounded-2xl shadow-sm border border-slate-100 bg-white p-5 md:p-6 mb-8">
					<label htmlFor="newSenderEmail" className="block text-sm font-medium text-slate-700 mb-1.5">
						Add a new sender
					</label>
					<div className="flex flex-col sm:flex-row gap-3">
						<input
							id="newSenderEmail"
							type="email"
							value={newEmail}
							onChange={(e) => setNewEmail(e.target.value)}
							placeholder="founder@yourdomain.com"
							className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition"/>
						<button
							type="submit"
							disabled={adding || !newEmail}
							className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-3 font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition">
							<Plus size={15} weight="bold"/>
							{adding ? 'Sending…' : 'Add identity'}
						</button>
					</div>
					<p className="text-xs text-slate-500 mt-2">
						We'll trigger AWS SES to email a verification link to that address. The status will show as <span className="font-medium text-amber-700">Pending</span> until the recipient clicks it.
					</p>
				</form>

				{/* List */}
				{loading ? (
					<div className="space-y-3">
						{[0, 1, 2].map((k) => (
							<div key={k} className="rounded-2xl shadow-sm border border-slate-100 bg-white p-5 animate-pulse">
								<div className="flex items-center justify-between gap-4">
									<div className="h-4 w-1/3 bg-slate-100 rounded"/>
									<div className="h-6 w-20 bg-slate-100 rounded-full"/>
								</div>
								<div className="mt-3 h-3 w-24 bg-slate-100 rounded"/>
							</div>
						))}
					</div>
				) : items.length === 0 ? (
					<div className="rounded-2xl shadow-sm border border-slate-100 bg-white p-10 text-center">
						<div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-50 mb-4">
							<EnvelopeOpen size={32} weight="duotone" color="#10B981"/>
						</div>
						<h3 className="text-lg font-semibold text-slate-900">Add your first sender</h3>
						<p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
							You don't have any verified sender addresses yet. Add one above and we'll guide you through AWS SES verification.
						</p>
						<button
							type="button"
							onClick={scrollToForm}
							className="mt-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-3 font-semibold inline-flex items-center gap-1.5 transition">
							<Plus size={15} weight="bold"/>
							Add sender
						</button>
					</div>
				) : (
					<ul className="space-y-3">
						{items.map((it) => {
							const busy = rowBusy[it.email];
							return (
								<li
									key={it.email}
									className="rounded-2xl shadow-sm border border-slate-100 bg-white p-5 flex items-center justify-between gap-4">
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2 min-w-0">
											<span className="text-sm md:text-base font-medium text-slate-900 truncate">{it.email}</span>
										</div>
										<div className="mt-2 flex items-center gap-3 text-xs">
											<button
												type="button"
												onClick={() => refreshRow(it.email)}
												disabled={!!busy}
												className="text-emerald-700 hover:text-emerald-800 font-medium inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
												<ArrowClockwise size={12} weight="bold" className={busy === 'check' ? 'animate-spin' : ''}/>
												{busy === 'check' ? 'Checking…' : 'Refresh status'}
											</button>
											<span className="text-slate-300">·</span>
											<button
												type="button"
												onClick={() => removeRow(it.email)}
												disabled={!!busy}
												className="text-slate-500 hover:text-red-600 font-medium inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
												<Trash size={12} weight="bold"/>
												{busy === 'remove' ? 'Removing…' : 'Remove'}
											</button>
										</div>
									</div>
									<StatusBadge status={it.status}/>
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</div>
	);
}
